import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  Form,
  Modal,
  message,
  DatePicker,
  Select,
  Radio,
  Alert,
} from "antd";
import moment from "moment";
import Service from "../../../service";
import { Option } from "antd/lib/mentions";
import { isWeekoffDay, hasWeekoffDays, resolveWeekConfigForDate } from "../../../util/weekConfig";
import { showAuthLoader, hideAuthLoader } from "../../../appRedux/actions/Auth";
import { useDispatch, useSelector } from "react-redux";
import useCompanyDateFormat from "../../../hooks/useCompanyDateFormat";

const { TextArea } = Input;

// Singular unit noun per frequency, used for the "N allowed per <unit>" preview
// line - distinct from the capitalized "Weekly"/"Monthly" labels used in the
// WFH Settings admin screens.
const WFH_FREQUENCY_UNIT_LABEL = {
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  half_yearly: "half-year",
  yearly: "year",
};

const AddWfhRequestModal = ({ show, onClose, onSuccess }) => {
  const [form] = Form.useForm();
     const formValues = Form.useWatch([], form);
          
          const hasAnyValue = formValues
          ? Object.values(formValues).some((v) => v !== undefined && v !== null && v !== "")
          : false;
  const [dates, setDates] = useState("");
  const [employeeData, setEmployeeData] = useState({});
  const [days, setDays] = useState("");
  const [reportingManager, setReportingManager] = useState("");
  const [floatflag, setFloatflag] = useState(false);
  const [alldate, setAlldate] = useState([]);
  const [wfh_type, setwfh_type] = useState([]);
  const [workweekTimeline, setWorkweekTimeline] = useState([]);
  // WFH policy preview - read-only, informational. The server (AddWFHApplication)
  // is the real source of enforcement, so a failure here never blocks submission,
  // unlike ResignationFlow's notice period resolve which blocks on error.
  const [wfhPolicy, setWfhPolicy] = useState(null);
  const [wfhPolicyMessage, setWfhPolicyMessage] = useState(null);
  const [wfhPolicyLoading, setWfhPolicyLoading] = useState(false);
  const [wfhPolicyError, setWfhPolicyError] = useState(null);
  // A 7-day working shift has no weekoff days, so "Including Weekoff" cannot change
  // anything - every date is counted either way. Hide it and pin the value so the
  // required-field rule still passes.
  const shiftHasNoWeekoffs = workweekTimeline.length > 0 && !workweekTimeline.some((entry) => hasWeekoffDays({ pattern: entry?.pattern }));

  const pattern = /(^[0-9]\.5$)|(^[0-9][0-9]*(\.[05])?$)/;
  const dispatch = useDispatch();
  const dateFormat = useCompanyDateFormat();
  const { authUser } = useSelector(({ auth }) => auth);

  let sendData;
  let WFHDataArray;

  const getDates = (startDate, endDate) => {
    let datesArray = [];
    var currentDate = moment(startDate, "DD-MM-YYYY");
    const stopDate = moment(endDate, "DD-MM-YYYY");

    while (currentDate <= stopDate) {
      datesArray.push({
        particular_date: moment(new Date(currentDate)).format("MM-DD-YYYY"),
        wfh_type: form.getFieldValue("wfh_type"),
      });
      currentDate.add(1, 'days');
    }
    return datesArray;
  };

  let leaveDays = [];
  const calculateToDate = () => {
    if (dates && days) {
      let startDate = moment(dates?._d);
      let count = 0;
      const totalDays = Math.floor(days);
      const isHalfDay = days % 1 !== 0;
      const includeWeekoff = form.getFieldValue("is_weekoff") === "true";

      // Weekoff days come from the employee's work week, not a hardcoded
      // Sat/Sun - otherwise WFH day counting is wrong for every non-standard shift.

      while (count < totalDays) {
        if (includeWeekoff || !isWeekoffDay(startDate, resolveWeekConfigForDate(null, workweekTimeline, startDate))) {
          leaveDays.push(moment(startDate).format("DD-MM-YYYY"));
          count++;
        }
        startDate.add(1, "days");
      }

      if (isHalfDay) {
        // Guard the scan: a 7-day shift has no weekoffs, and a misconfigured
        // all-weekoff config would otherwise loop forever.
        let skipGuard = 0;
        while (!includeWeekoff && isWeekoffDay(startDate, resolveWeekConfigForDate(null, workweekTimeline, startDate)) && skipGuard < 14) {
          startDate.add(1, "days");
          skipGuard++;
        }
        leaveDays.push(moment(startDate).format("DD-MM-YYYY"));
      }

      const finalEndDate = days == "0.5" ? startDate : floatflag && pattern.test(days) ? moment(startDate) : moment(startDate).add(-1, "days");

      WFHDataArray = includeWeekoff
        ? getDates(dates, finalEndDate)
        : getDatesWithoutWeekends(dates, finalEndDate);

      sendData = finalEndDate;
      return finalEndDate;
    } else {
      return dates;
    }
  };

  function getDatesWithoutWeekends(startDate, endDate) {
    var dates = [];
    // moment (not a native Date) so weekoff resolution uses the shared helper
    var currentDate = moment(startDate);
    const stopDate = moment(endDate);
    while (currentDate.isSameOrBefore(stopDate, "day")) {
      if (!isWeekoffDay(currentDate, resolveWeekConfigForDate(null, workweekTimeline, currentDate))) {
        dates.push({
          particular_date: currentDate.format("MM-DD-YYYY"),
          wfh_type: form.getFieldValue("wfh_type"),
        });
      }
      currentDate.add(1, "days");
    }
    return dates;
  }

  const getreportingmanager = async () => {
    const params = `/${authUser?._id}`;
    const response = await Service.makeAPICall({
      methodName: Service.getMethod,
      api_url: Service.reportingmanager + params,
    });

    if (response?.data && response?.data?.data) {
      setReportingManager(response?.data?.data);
    }
  };

  const fetchEmployeeDetails = async () => {
    try {
      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.empById + `/${authUser._id}`
      });
      if (response?.data && response.data.status === 200) {
        const empData = response?.data?.data;
        setEmployeeData(empData);
      } else {
        message.error(response.data.message);
      }
      dispatch(hideAuthLoader());
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log(error);
    }
  };

  const fetchWorkweekTimeline = async () => {
    try {
      if (!authUser?._id) return;
      const empResponse = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.empById + "/" + authUser._id,
      });
      setWorkweekTimeline(empResponse?.data?.data?.workweekTimeline || []);
    } catch (error) {
      console.error("Error fetching work week timeline:", error);
    }
  };

  useEffect(() => {
    if (show) {
      getreportingmanager();
      fetchEmployeeDetails();
      fetchWorkweekTimeline();
    }
  }, [show]);

  // Preview which WFH policy (if any) applies to the employee - mirrors
  // ResignationFlow's resolveNoticePeriod effect exactly (same shape: loading
  // + error state, POST { employee_id }). Unlike notice period, this is purely
  // informational: the server enforces wfh_count in AddWFHApplication, so a
  // resolve failure here must not block the form.
  useEffect(() => {
    const resolveWfhPolicy = async () => {
      if (!authUser?._id) return;
      setWfhPolicyLoading(true);
      setWfhPolicyError(null);
      try {
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.resolveWfhPolicy,
          body: { employee_id: authUser._id },
        });
        if (response?.data?.status === 200 && response?.data?.data) {
          setWfhPolicy(response.data.data.policy || null);
          setWfhPolicyMessage(response.data.data.message || null);
        } else {
          setWfhPolicyError(
            response?.data?.message || "Could not determine your WFH policy."
          );
        }
      } catch (err) {
        setWfhPolicyError("Could not determine your WFH policy.");
      } finally {
        setWfhPolicyLoading(false);
      }
    };
    resolveWfhPolicy();
  }, [authUser?._id]);

  useEffect(() => {
    if (shiftHasNoWeekoffs) {
      form.setFieldsValue({ is_weekoff: "true" });
    }
  }, [shiftHasNoWeekoffs, form]);

  const handleSubmit = async (values) => {
    try {
      delete values.days;
      const reqBody = {
        ...values,
        custompartday: moment(values?.custompartday).format("MM-DD-YYYY"),
        employee_id: authUser?._id,
        from_date: moment(values?.from_date).format("MM-DD-YYYY"),
        reporting_manager: authUser?.reporting_manager,
        application_date: moment().format("MM-DD-YYYY"),
        to_date: sendData
          ? moment(sendData).format("MM-DD-YYYY")
          : moment(dates).format("MM-DD-YYYY"),
        is_weekoff: values.is_weekoff == "true" ? true : false,
        wfh_dates:
          alldate.length > 0
            ? alldate.map((item) => ({
              ...item,
              wfh_type: item.wfh_type == undefined || item.wfh_type == "" ? values.wfh_type : item.wfh_type,
              particular_date: moment(item.particular_date).format("MM-DD-YYYY"),
            }))
            : WFHDataArray
              ? WFHDataArray.map((item) => ({
                ...item,
                wfh_type: values.wfh_type ? values.wfh_type : "Full Day",
                particular_date: moment(item.particular_date).format("MM-DD-YYYY"),
              }))
              : [
                {
                  particular_date: moment(dates).format("MM-DD-YYYY"),
                  wfh_type: wfh_type ? wfh_type : "Part Day",
                },
              ]
      };

      dispatch(showAuthLoader());
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.addwfh,
        body: reqBody,
      });
      dispatch(hideAuthLoader());

      if (response.data && response.data.data) {
        message.success(response.data.message);
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      dispatch(hideAuthLoader());
      console.log(error);
    }
  };

  const onDateChange = (val) => {
    form.setFieldsValue({
      custompartday: "",
    });
    setDates(val);
  };

  // The matched WFH rule decides how far back the From Date may go; no rule
  // (or no setting on it) keeps today as the earliest selectable date.
  function disabledDate(current) {
    const backdateAllowDays = Number(wfhPolicy?.backdate_allow_days) || 0;
    const earliestAllowed = moment().startOf('day').subtract(backdateAllowDays, 'days');
    return current && current < earliestAllowed;
  }

  const checkfloat = (num) => {
    let realnum = num * 1;
    realnum > 1 && realnum % 1 != 0 ? setFloatflag(true) : setFloatflag(false);
  };

  const handleClose = () => {
    form.resetFields();
    setDates("");
    setDays("");
    setAlldate([]);
    setwfh_type([]);
    setFloatflag(false);
    onClose();
  };

  return (
    <Modal
    title="Add WFH Application"
    visible={ show }
    onCancel={ handleClose }
    width={ 800 }
    footer={ [
      <Button key="cancel" className="ant-delete" onClick={ handleClose }>
        Cancel
      </Button>,
      <Button
        key="clear"
        className="ant-delete"
        onClick={ () => {
          form.resetFields();
          setDates("");
          setDays("");
          setAlldate([]);
        } }
        disabled={!hasAnyValue} 
        
      >
      Reset
      </Button>,
      <Button
        key="submit"
        type="primary"
        htmlType="submit"
        onClick={ () => form.submit() }
        disabled={
          employeeData.emp_type == "Probation" ||
          (employeeData.left_date !== null && employeeData.emp_last_working_date !== null)
        }
        data-tour="wfh-save-btn"
      >
        Save
      </Button>,
    ] }
    destroyOnClose
  >
    <Form form={ form } layout="vertical" onFinish={ handleSubmit }>
      <div className="main-form-wrapper">
        <div className="form-container">
          <div className="form-row">
            <div className="form-col">
              <div className="form-item">
                <Form.Item label="Application Date" name="application_date">
                  <span>{ moment().format(dateFormat) }</span>
                </Form.Item>
              </div>
            </div>
            <div className="form-col">
              <div className="form-item">
                <Form.Item label="Reporting Manager" name="reporting_manager">
                  <span>{ reportingManager?.reporting_manager?.full_name }</span>
                </Form.Item>
              </div>
            </div>
          </div>
          { !wfhPolicyLoading && (wfhPolicyMessage || wfhPolicyError) && (
            <div className="form-row">
              <div className="form-col-full">
                <Alert
                  type={ wfhPolicyError ? "warning" : "info" }
                  showIcon
                  banner
                  message={
                    wfhPolicyError
                      ? wfhPolicyError
                      : wfhPolicy
                        ? `WFH rule "${wfhPolicy.name}": ${wfhPolicy.wfh_count} allowed per ${
                          WFH_FREQUENCY_UNIT_LABEL[wfhPolicy.frequency] || wfhPolicy.frequency
                        }.`
                        : "No WFH limit rule applies to you: unlimited WFH."
                  }
                  style={{ marginBottom: 4 }}
                />
              </div>
            </div>
          ) }
          <div className="form-row">
            <div className="form-col" data-tour="wfh-from-date">
              <div className="form-item">
                <Form.Item
                  label="From Date"
                  name="from_date"
                  rules={ [{ required: true, message: "Please enter your Application Dates!" }] }
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    className="fulldate-width"
                    onChange={ onDateChange }
                    disabledDate={ disabledDate }
                  />
                </Form.Item>
              </div>
            </div>
            <div className="form-col" data-tour="wfh-weekoff">
              <div className="form-item">
                {shiftHasNoWeekoffs ? (
                <Form.Item label="Including Weekoff">
                  <span style={{ color: "#6B7280" }}>
                    Your work week has no weekoff days, so every day is counted.
                  </span>
                </Form.Item>
                ) : (
                <Form.Item
                  label="Including Weekoff"
                  name="is_weekoff"
                  initialValue="false"
                  rules={ [{ required: true, message: "Please select an option!" }] }
                >
                  <Radio.Group
                    onChange={ (e) => {
                      form.setFieldsValue({ is_weekoff: e.target.value });
                      setDays("");
                      form.setFieldsValue({ no_of_days: "" });
                      form.setFieldsValue({ wfh_type: "" });
                      form.setFieldsValue({ custompartday: "" });
                      form.setFieldsValue({
                        to_date: moment(calculateToDate()).format(dateFormat),
                      });
                    } }
                  >
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </Radio.Group>
                </Form.Item>
                )}
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-col" data-tour="wfh-days">
              <div className="form-item">
                <Form.Item
                  label="No. of Days"
                  name="no_of_days"
                  rules={ [
                    { required: true, message: "Please enter the number of Days!" },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const floatValue = parseFloat(value);
                        if (floatValue === 0) {
                          return Promise.reject("Min WFH can be taken is 0.5");
                        }
                        if (floatValue < 0) {
                          return Promise.reject("Days should not be negative");
                        }
                        const validPattern = /^(0.5|[1-9][0-9]*(\.5)?)$/;
                        if (!validPattern.test(value)) {
                          return Promise.reject("Only multiples of 0.5 are allowed (e.g., 0.5, 1, 1.5, 2).");
                        }
                        return Promise.resolve();
                      },
                    },
                  ] }
                >
                  <Input
                    className="fulldate-width"
                    type="number"
                    controls={ false }
                    onBlur={ (e) => {
                      if (e.target.value == 0) {
                        message.error("Min WFH can be taken is 0.5");
                        setDays("");
                        setAlldate([]);
                        form.setFieldsValue({ no_of_days: "" });
                      }
                    } }
                    onChange={ (e) => {
                      checkfloat(e.target.value);
                      setAlldate([]);
                      setDays(e.target.value);
                      form.setFieldsValue({
                        to_date: moment(calculateToDate()).format(dateFormat),
                      });
                    } }
                  />
                </Form.Item>
              </div>
            </div>
            <div className="form-col">
              <div className="form-item">
                <Form.Item label="To Date" name="to_date">
                  <span style={{ color: (days && dates) ? undefined : "#9CA3AF" }}>
                    { (days && dates) ? moment(calculateToDate()).format(dateFormat) : "-" }
                  </span>
                </Form.Item>
              </div>
            </div>
          </div>
          { floatflag && pattern.test(days) && (
            <div className="form-row">
              <div className="form-col">
                <div className="form-item">
                  <Form.Item
                    name="custompartday"
                    label="Date For Half Day"
                    rules={ [{ required: true, message: "Please Select the date for your half day leave!" }] }
                  >
                    <Select
                      onChange={ (e) => {
                        let finalLeaveDatesArray = WFHDataArray.map((val) => {
                          return val.particular_date !== e
                            ? { ...val, wfh_type: "Full Day" }
                            : { ...val, wfh_type: form.getFieldValue("wfh_type") };
                        });
                        setAlldate(finalLeaveDatesArray);
                      } }
                      defaultValue="Select"
                    >
                      { WFHDataArray?.map((item, index) => (
                        <Option key={ index } value={ item.particular_date }>
                          { moment(item.particular_date).format(dateFormat) }
                        </Option>
                      )) }
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="form-col">
                <div className="form-item">
                  <Form.Item
                    label="WFH type"
                    name="wfh_type"
                    rules={ [{ required: true, message: "Please enter WFH type!" }] }
                  >
                    <Select
                    defaultValue="Select"
                      onChange={ (e) => {
                        setwfh_type(e);
                      } }
                      options={ [
                        { value: "First Half", label: "First Half" },
                        { value: "Second Half", label: "Second Half" },
                      ] }
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          ) }
          { days == "0.5" && (
            <div className="form-row">
              <div className="form-col">
                <div className="form-item">
                  <Form.Item
                    label="WFH type"
                    name="wfh_type"
                    rules={ [{ required: true, message: "Please enter WFH type!" }] }
                  >
                    <Select
                    defaultValue="Select"
                      onChange={ (e) => {
                        setwfh_type(e);
                      } }
                      options={ [
                        { value: "First Half", label: "First Half" },
                        { value: "Second Half", label: "Second Half" },
                      ] }
                    />
                  </Form.Item>
                </div>
              </div>
              <div className="form-col">
                {/* Empty column for spacing */ }
              </div>
            </div>
          ) }
          <div className="form-row">
            <div className="form-col-full" data-tour="wfh-reason">
              <div className="form-item">
                <Form.Item
                  label="Reason"
                  name="reason"
                  rules={ [{ required: true, message: "Please enter your reason!" }] }
                >
                  <TextArea maxLength={ 100 } className="textarea-fixed-height" />
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  </Modal>
  );
};

export default AddWfhRequestModal;